import React from "react";
import { Shield, ChevronRight } from "lucide-react";
import PremiumHeader from "../components/ui/PremiumHeader";
import MembershipStatsCard from "../components/ui/MembershipStatsCard";
import { useEvents } from "../hooks";
import { useMypageContext } from "../context/MypageContext";
import { UI_TEXT } from "../constants/uiText";
import { splitEventsByTimeline } from "../utils/eventSections";

interface TournamentsViewProps {
  onStageClick: (name: string, eventId: string, isEventEnded: boolean) => void;
  onNotificationClick: () => void;
}

const TournamentsView: React.FC<TournamentsViewProps> = ({
  onStageClick,
  onNotificationClick,
}) => {
  const { events, isLoading, error } = useEvents();
  const { mypage } = useMypageContext();
  const { upNext, schedule, pastEvents } = splitEventsByTimeline(events);

  const renderEventRow = (event: (typeof events)[number]) => (
    <div
      key={event.id}
      onClick={() =>
        onStageClick(
          `${event.tournament} ${event.title}`,
          event.id,
          event.status === "finished",
        )
      }
      className="flex items-center justify-between p-4 mb-3 bg-white border border-slate-100 rounded-2xl hover:bg-amber-50 cursor-pointer transition-all group shadow-sm active:scale-[0.98]"
    >
      <div className="text-left min-w-0 flex-1">
        <div className="flex flex-col items-start gap-1">
          {event.status === "finished" && (
            <span className="inline-flex items-center text-[9px] font-black bg-slate-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">
              イベント終了
            </span>
          )}
          <p className="font-bold text-slate-700 group-hover:text-amber-600 transition-colors uppercase tracking-tight whitespace-normal break-words leading-snug">
            {`${event.tournament} ${event.title}`}
          </p>
          <p className="text-xs text-slate-400">{event.date}</p>
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-200 group-hover:text-amber-400 transition-colors shrink-0" />
    </div>
  );

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-300 overflow-hidden">
      <PremiumHeader
        title={UI_TEXT.nav.tournaments}
        icon={Shield}
        theme="amber"
        onNotificationClick={onNotificationClick}
      />
      <div className="px-6 py-6 pb-40 overflow-y-auto scrollbar-hide text-left flex-1">
        <MembershipStatsCard
          points={mypage?.yearly_point ?? "-"}
          rank={mypage?.yearly_rank ?? "-"}
          loginId={mypage?.login_id}
          dartsliveName={mypage?.dartslive_card_name}
          eventGroupName={mypage?.event_group?.name}
          theme="amber"
        />
        <h2 className="text-2xl font-black text-slate-800 tracking-tighter uppercase mb-4 pl-1">
          イベント一覧
        </h2>
        <p className="text-xs text-slate-400 mb-8 pl-1">
          {UI_TEXT.tournaments.tournamentCreationNotice}
        </p>

        {isLoading && (
          <p className="text-slate-400 text-sm text-center py-10">
            読み込み中...
          </p>
        )}
        {error && (
          <p className="text-rose-400 text-sm text-center py-10">{error}</p>
        )}

        {!isLoading && !error && upNext && (
          <>
            <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 pl-1 text-left">
              {UI_TEXT.dashboard.upNext}
            </h3>
            {renderEventRow(upNext)}
          </>
        )}
        {!isLoading && !error && schedule.length > 0 && (
          <>
            <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-10 mb-4 pl-1 text-left">
              {UI_TEXT.dashboard.schedule}
            </h3>
            {schedule.map(renderEventRow)}
          </>
        )}
        {!isLoading && !error && pastEvents.length > 0 && (
          <>
            <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-10 mb-4 pl-1 text-left">
              {UI_TEXT.dashboard.pastEvents}
            </h3>
            {pastEvents.map(renderEventRow)}
          </>
        )}
      </div>
    </div>
  );
};

export default TournamentsView;
